import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { lighten } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
/** import mui components in the section above, Sort Lines Ascending */

const useStyles = makeStyles(theme => ({
    tacticHeader: {
        color: theme.palette.primary.main,
        backgroundColor: lighten(theme.palette.primary.light, 0.85),
        display: 'flex',
        justifyContent: 'space-between',
    },
    tacticsFooter: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    targetingCard: {
        margin: 15,
    },
    targetingHeader: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.grey[100],
        display: 'flex',
        justifyContent: 'space-between',
    },
    andButton: {
        display: 'flex',
        justifyContent: 'center',
    },
    orButton: {
        display: 'flex',
        justifyContent: 'center',
        margin: 10,
    },
    andOrButton: {
        display: 'flex',
        justifyContent: 'center',
    },
    removeTooltip: {
        ...theme.typography.body2,
        padding: theme.spacing(1, 1.5),
    },
}));

// OrCard
export function OrCard({ children }) {
    return <Paper>{children}</Paper>;
}

/**
 * Header row for an OR-card with optional remove control.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Title or header content (e.g. rule/condition label)
 * @param {Function} [props.onRemove] - Called when the delete button is clicked
 * @param {boolean} [props.showRemove=true] - When false, hides the delete button (e.g. default rule rows)
 * @param {string} [props.removeTooltipTitle] - Tooltip and aria-label for the delete button; omit for no tooltip
 * @returns {React.ReactElement}
 */
export function OrCardHeader({ children, onRemove, showRemove = true, removeTooltipTitle }) {
    const classes = useStyles();

    const removeButton = (
        <IconButton onClick={onRemove} size="large" aria-label={removeTooltipTitle || 'Remove'}>
            <DeleteIcon />
        </IconButton>
    );

    return (
        <Toolbar className={classes.tacticHeader}>
            <span>{children}</span>
            {showRemove &&
                (removeTooltipTitle ? (
                    <Tooltip
                        title={removeTooltipTitle}
                        placement="top"
                        classes={{ tooltip: classes.removeTooltip }}
                    >
                        {removeButton}
                    </Tooltip>
                ) : (
                    removeButton
                ))}
        </Toolbar>
    );
}

export function OrCardBody({ children }) {
    return <div>{children}</div>;
}

export function OrCardFooter({ children }) {
    const classes = useStyles();
    return <CardActions className={classes.tacticsFooter}>{children}</CardActions>;
}

// AndCard
export function AndCard({ children }) {
    const classes = useStyles();
    return <Card className={classes.targetingCard}>{children}</Card>;
}

export function AndCardHeader({ children, onRemove }) {
    const classes = useStyles();
    return (
        <Toolbar className={classes.targetingHeader}>
            <span>{children}</span>
            <IconButton onClick={onRemove} size="large">
                <DeleteIcon />
            </IconButton>
        </Toolbar>
    );
}

export function AndCardBody({ children }) {
    return <CardContent>{children}</CardContent>;
}

export function AndCardFooter({ children }) {
    return <div>{children}</div>;
}

export function AndOrButton({ children }) {
    const classes = useStyles();
    return (
        <Card className={classes.andOrButton}>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

export function AndDivider() {
    const classes = useStyles();
    return (
        <div className={classes.andButton}>
            <Chip label="AND" />
        </div>
    );
}

export function OrDivider() {
    const classes = useStyles();
    return (
        <div className={classes.orButton}>
            <Chip label="OR" />
        </div>
    );
}
